import React, { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import env from '@beam-australia/react-env';
import ExclamationMarkOrange from 'assets/images/icons/exclamation-mark-orange.svg';
import phoneIcon from 'assets/images/icons/phone.svg';
import { PAYFAIL_OVERVIEW, PAYFAIL_ONGOING } from 'constants/routeConstants';
import {
  PAYFAIL_CALL_US_NUMBER, PAYFAIL_CALL_US_HREF, PAYFAIL_CALL_US_TITLE, PAYFAIL_CALL_US_BODY,
  PAYFAIL_CALL_US_EARLY_CANCELLED_TITLE, PAYFAIL_CALL_US_EARLY_CANCELLED_BODY,
} from 'constants/payfailConstants';
import trackEvent from 'services/AnalyticsService';
import { Store } from 'types/store';
import styles from './PayfailModal.module.scss';

interface IPayfailModalProps {
  payfailEligibility: {
    canProceedDireclyToReactivation: boolean
  };
  updatePayfailPopupShown: (_shown: boolean) => void;
}

const PayfailModal: React.FC<IPayfailModalProps> = ({ payfailEligibility, updatePayfailPopupShown }) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(true);
  const { writeOffAndDiscountPercentage, isEarlyCancelledMandate, isMonthlyOnCard, isEligibleForOnlineResolution } = useSelector((state: Store) => state?.session?.payfailAlertData) || {};

  // Determine step to redirect user to
  const linkRef = payfailEligibility?.canProceedDireclyToReactivation ? PAYFAIL_ONGOING : PAYFAIL_OVERVIEW;
  const hasDiscount = Boolean(writeOffAndDiscountPercentage);

  let title = 'Your payment has failed';
  let buttonText = 'Make payment';
  let description = (
    <p className="text-center">
      Your last payment for breakdown cover didn’t go through.<br />
      {hasDiscount && (
        <>Let’s fix it - <b>pay now and save {writeOffAndDiscountPercentage}%.</b></>
      )}
    </p>
  );

  // Members who can't resolve online are asked to call, whatever else applies
  const isCallUs = isEligibleForOnlineResolution === false;
  const eventPrefix = isCallUs ? 'MyRAC_PayfailCallUs' : 'MyRAC_Payfail';

  if (isCallUs) {
    title = isEarlyCancelledMandate ? PAYFAIL_CALL_US_EARLY_CANCELLED_TITLE : PAYFAIL_CALL_US_TITLE;
    description = (
      <p className="text-center px-2">
        {isEarlyCancelledMandate ? PAYFAIL_CALL_US_EARLY_CANCELLED_BODY : PAYFAIL_CALL_US_BODY}
      </p>
    );
  } else if (isMonthlyOnCard) {
    title = 'Restart your monthly card payments';
    buttonText = 'Set up now';
    description = (
      <p className="text-center">
        Never miss a payment again
        {hasDiscount && (
          <> &mdash; and <b>save {writeOffAndDiscountPercentage}%</b></>
        )}
      </p>
    );
  } else if (isEarlyCancelledMandate) {
    title = 'Restart your direct debit';
    buttonText = 'Restart now';
    description = (
      <p className="text-center px-2">
        We noticed you cancelled your direct debit. Restart it today to keep your breakdown cover
        {hasDiscount && (
          <> and we’ll take <b>{writeOffAndDiscountPercentage}% off your price</b></>
        )}.
      </p>
    );
  }

  const handleClose = () => {
    if (isCallUs) trackEvent(`${eventPrefix}_PopUp_Closed`);
    updatePayfailPopupShown(true);
    setIsOpen(false);
  };

  const handleAction = () => {
    trackEvent('MyRAC_Payfail_PopUp_Clicked');
    updatePayfailPopupShown(true);
    window.location.href = `${env('HOME_URL')}${linkRef}`
  };

  const handleCallClick = () => {
    trackEvent(`${eventPrefix}_PopUp_CallClicked`);
  };

  useEffect(() => {
    if (isOpen) {
      trackEvent(`${eventPrefix}_PopUp_Displayed`);
      popupRef.current?.focus();
    }
  }, [isOpen, eventPrefix]);

  if (!isOpen) return null;

  return (
    <Modal
      show={isOpen}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header className="px-4 pt-4 pb-0 border-0">
        <div className={styles.actionHeader}>
          <img
            src={ExclamationMarkOrange}
            alt="Payfail warning"
          />
          Action needed
        </div>
      </Modal.Header>
      <Modal.Body className="px-4 pt-4 pb-0">
        <div>
          <h2 className={clsx(styles.title, isMonthlyOnCard && styles.titleWrap)}>{title}</h2>
          {description}
        </div>
      </Modal.Body>
      <Modal.Footer className="d-block w-100 p-4 border-0">
        {isCallUs ? (
          <div className={styles.inlineAction}>
            <a className={styles.callLink} href={PAYFAIL_CALL_US_HREF} onClick={handleCallClick}>
              <img src={phoneIcon} alt="" />
              {PAYFAIL_CALL_US_NUMBER}
            </a>
          </div>
        ) : (
          <button type="button" className={styles.mainButton} onClick={handleAction}>{buttonText}</button>
        )}
        {isCallUs ? (
          <div className={styles.inlineAction}>
            <button type="button" className={clsx(styles.closeButton, styles.closeButtonInline)} onClick={handleClose}>Close</button>
          </div>
        ) : (
          <button type="button" className={styles.closeButton} onClick={handleClose}>Close</button>
        )}
      </Modal.Footer>
    </Modal>

  );
};

export default PayfailModal;
